/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PollingEntry, PollingTaskStatus, PollingUiStatus } from './types'
import * as pollingEngine from './polling-engine'

type LocalDispatch = (action: any) => any

const STORAGE_KEY = 'pollingStatus'

const loadPersistedTasks = (): PollingEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export interface PollingStatusState {
  tasks: PollingEntry[]
}

const initialState: PollingStatusState = {
  tasks: loadPersistedTasks(),
}

const slice = createSlice({
  name: 'pollingStatus',
  initialState,
  reducers: {
    started(state, action: PayloadAction<PollingEntry>) {
      const idx = state.tasks.findIndex((t) => t.id === action.payload.id)
      if (idx >= 0) {
        state.tasks[idx] = action.payload
      } else {
        state.tasks.push(action.payload)
      }
    },
    updated(
      state,
      action: PayloadAction<{ id: string; result: PollingTaskStatus }>,
    ) {
      const { id, result } = action.payload
      const task = state.tasks.find((t) => t.id === id)
      if (!task) return
      task.uiStatus = result.status as PollingUiStatus
      task.message = result.message ?? null
      task.errors = result.errors ?? []
      const newMeta = { ...result.meta }
      if (!newMeta.iniciadoEm && task.meta.iniciadoEm) {
        newMeta.iniciadoEm = task.meta.iniciadoEm
      }
      task.meta = newMeta
    },
    dismissed(state, action: PayloadAction<string>) {
      state.tasks = state.tasks.filter((t) => t.id !== action.payload)
    },
    dismissedAll(state) {
      state.tasks = []
    },
  },
})

export const {
  started, updated, dismissed, dismissedAll,
} = slice.actions

export const startPolling = (config: {
  id: string
  title: string
  fetchStatus: () => Promise<PollingTaskStatus>
  params?: Record<string, unknown>
  intervalMs?: number
}) => (dispatch: LocalDispatch): void => {
  const intervalMs = config.intervalMs ?? 10000
  const params = config.params ?? {}

  const entry: PollingEntry = {
    id: config.id,
    title: config.title,
    uiStatus: 'loading',
    message: null,
    errors: [],
    meta: {},
    startedAt: Date.now(),
    params,
    intervalMs,
  }

  dispatch(started(entry))
  pollingEngine.start(config.id, config.fetchStatus, intervalMs, dispatch)
}

export const dismissAllPolling = () => (
  dispatch: LocalDispatch,
  getState: () => any,
): void => {
  const { tasks } = getState().pollingStatus as PollingStatusState
  tasks.forEach((t) => {
    if (t.uiStatus === 'loading') pollingEngine.stop(t.id)
  })
  dispatch(dismissedAll())
}

export default slice.reducer
